/**
 * ENG-436 — the local half of search-vs-chat routing.
 *
 * Pure decision logic: no network, no store, no router. Kept apart from
 * `use-query-router` so a component that only needs the keyboard mapping
 * doesn't pull the API client (and the auth store behind it) into its
 * module graph.
 */

/* ---------------------------------------------------------------------------------------------- */

/** Which of the two interfaces should service a query. */
export type QueryMode = 'chat' | 'search';

/** Why a query ended up where it did. Surfaced in the UI and in analytics. */
export type QueryRouteSource = 'override' | 'heuristic' | 'llm' | 'fallback';

export interface QueryRoute {
   mode: QueryMode;
   /** Keyword query to run when `mode` is `search`. Refined where possible. */
   searchQuery: string;
   /** Message to send when `mode` is `chat`. Always the user's own words. */
   message: string;
   source: QueryRouteSource;
   /** Identifier of the rule that decided, for debugging and telemetry. */
   rule: string;
}

/* ---------------------------------------------------------------------------------------------- */
/* Heuristics                                                                                      */

export interface QueryHeuristicsConfig {
   /** Inputs with fewer words than this are treated as keyword searches. */
   minChatWords: number;
   /** Leading words that mark an input as a request for a written answer. */
   questionLeads: string[];
   /**
    * Words that mark a request for a written answer *wherever* they appear.
    *
    * A document list can't answer "compliance history" or "DP-1353 summary" —
    * both name a body of work to be read and synthesised, not a term to match.
    * Distinct from `questionLeads` because these outrank the shape rules
    * (identifier, word count) that would otherwise claim such inputs for search.
    */
   analysisTerms: string[];
   /** Matches permit / EPA / docket style identifiers, e.g. DP-1353, NM0030759. */
   identifierPattern: RegExp;
}

/**
 * Defaults, exported so they can be tuned in one place — and overridden
 * per-call via `heuristicRoute(input, overrides)`.
 */
export const QUERY_HEURISTICS: QueryHeuristicsConfig = {
   minChatWords: 3,
   questionLeads: [
      'what',
      'why',
      'how',
      'when',
      'who',
      'whom',
      'whose',
      'which',
      'where',
      'is',
      'are',
      'was',
      'were',
      'do',
      'does',
      'did',
      'can',
      'could',
      'should',
      'would',
      'will',
      'has',
      'have',
      'had',
      'explain',
      'summarize',
      'summarise',
      'compare',
      'describe',
      'tell',
      'list',
      'draft',
      'write',
      'analyze',
      'analyse',
   ],
   // Nouns first, then the verbs `questionLeads` only catches in first position
   // ("please summarize the enforcement actions" leads with "please").
   analysisTerms: [
      'history',
      'historical',
      'summary',
      'summaries',
      'timeline',
      'chronology',
      'overview',
      'background',
      'recap',
      'trend',
      'trends',
      'comparison',
      'analysis',
      'explanation',
      'implications',
      'significance',
      'rationale',
      'takeaways',
      'summarize',
      'summarise',
      'compare',
      'explain',
      'describe',
      'analyze',
      'analyse',
   ],
   identifierPattern: /\b[a-z]{2,5}-?\d{3,}\b/i,
};

/** Reduce a word to its comparable core: lowercase letters and apostrophes. */
function normalizeWord(word: string): string {
   return word.toLowerCase().replace(/[^a-z']/g, '');
}

export interface HeuristicVerdict {
   mode: QueryMode;
   rule: string;
}

/**
 * Apply the local heuristics to an input.
 *
 * Returns `null` when no rule is confident enough to decide — that's the
 * signal to spend an LLM round on it.
 *
 * Rules are ordered most-specific first, and the two chat signals — question
 * phrasing and analytical wording — both beat identifier shape and word count,
 * so "what's in DP-1353?" and "DP-1353 compliance history" read as requests for
 * an answer rather than document lookups. Explicit quoting is the one search
 * gesture that outranks analytical wording: `"compliance history"` in quotes
 * asks for that exact phrase.
 */
export function heuristicRoute(
   input: string,
   overrides: Partial<QueryHeuristicsConfig> = {},
): HeuristicVerdict | null {
   const config = { ...QUERY_HEURISTICS, ...overrides };
   const trimmed = input.trim();
   if (!trimmed)
      return null;

   if (trimmed.endsWith('?'))
      return { mode: 'chat', rule: 'question-mark' };

   const words = trimmed.split(/\s+/);
   // A lead word only implies a question when something follows it — "list"
   // on its own is a keyword, "list the open permits" is a request.
   if (words.length > 1 && config.questionLeads.includes(normalizeWord(words[0])))
      return { mode: 'chat', rule: 'question-word' };

   if (/^".+"$/.test(trimmed) || /^'.+'$/.test(trimmed))
      return { mode: 'search', rule: 'quoted-phrase' };

   // Same "something else follows it" test as the lead words: a bare "history"
   // is a term to match, while "enforcement history" asks about a subject.
   if (words.length > 1 && words.some(word => config.analysisTerms.includes(normalizeWord(word))))
      return { mode: 'chat', rule: 'analysis-term' };

   if (config.identifierPattern.test(trimmed))
      return { mode: 'search', rule: 'identifier' };

   if (words.length < config.minChatWords)
      return { mode: 'search', rule: 'too-short' };

   return null;
}

/* ---------------------------------------------------------------------------------------------- */
/* Route notices                                                                                   */

/**
 * Reduce a query to the words a reader would recognise, so a refinement that
 * only changed capitalisation, spacing or punctuation doesn't read as a rewrite.
 */
function comparableQuery(value: string): string {
   return value
      .toLowerCase()
      .replace(/["'?!.,;:]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
}

/**
 * Whether landing on the search results is worth explaining to the user.
 *
 * The notice earns its place in exactly one situation: the search we ran is not
 * the text they typed. Then the search box holds words they never wrote, and
 * saying where those came from is the difference between helpful and baffling.
 *
 * Everything else stays quiet, because there is nothing the user doesn't already
 * know. A heuristic-routed search — an identifier, a quoted phrase, two bare
 * keywords — put their own words in the box, so announcing a decision only
 * invites them to doubt one they never made. Same for a search they asked for
 * outright, and same for a classifier round that decided against changing their
 * wording: an unchanged query is indistinguishable from no routing at all, so
 * the honest report is silence.
 */
export function shouldExplainRoute(route: QueryRoute): boolean {
   if (route.mode !== 'search' || route.source !== 'llm')
      return false;

   return comparableQuery(route.searchQuery) !== comparableQuery(route.message);
}

/* ---------------------------------------------------------------------------------------------- */
/* Keyboard overrides                                                                              */

/**
 * Mac reads ⌘/⌥; everything else reads Ctrl/Alt.
 *
 * `navigator.platform` is deprecated but still the most reliable signal in
 * every browser we support, and a wrong guess only mislabels a hint.
 */
function isMacPlatform(): boolean {
   return typeof navigator !== 'undefined' && /mac/i.test(navigator.platform ?? '');
}

/**
 * The one sentence explaining that the submit control decides for you, and
 * which keystrokes override it.
 *
 * Lives here rather than in a component because it is shown on two different
 * submit buttons. Mac gets the glyphs — read fluently there and compact —
 * while everyone else gets the words.
 *
 * Note this is a *keyboard* affordance: on touch there is no way to press it,
 * which is why the same choice needs a visible home on small screens.
 */
export function queryModeHint(isMac: boolean = isMacPlatform()): string {
   const { search, chat } = modifierKeyLabels(isMac);
   return `We'll pick search or Luma for you. ${search} to search, ${chat} to ask Luma.`;
}

/** How to write each override's keystroke for the current platform. */
export function modifierKeyLabels(isMac: boolean = isMacPlatform()): Record<QueryMode, string> {
   return {
      search: isMac ? '⌘↵' : 'Ctrl+Enter',
      chat: isMac ? '⌥↵' : 'Alt+Enter',
   };
}

/**
 * Whether the device has no hover — a phone or tablet.
 *
 * Used to drop the keystroke labels from the mode menu: on a touch device the
 * shortcuts can't be pressed, so printing them is noise where the menu is the
 * only* way to force a mode.
 */
export function isTouchPrimary(): boolean {
   return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: none)').matches;
}

/**
 * Map a submit keystroke to a forced interface.
 *
 * Ctrl/⌘+Enter goes straight to search results, Alt/⌥+Enter straight to Luma.
 * A bare Enter returns `null`, meaning "analyse it for me".
 */
export function forcedModeFromEvent(event: KeyboardEvent | MouseEvent): QueryMode | null {
   if (event.ctrlKey || event.metaKey)
      return 'search';
   if (event.altKey)
      return 'chat';
   return null;
}
